import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { darkTheme, lightTheme } from './theme';

type ThemeMode = 'light' | 'dark';
type ThemePreference = ThemeMode | 'system';

interface ThemeContextProps {
    mode: ThemeMode; // effektiv genutztes Theme
    preference: ThemePreference; // light | dark | system
    setPreferenceState: (pref: ThemePreference) => void;
    theme: typeof lightTheme;
}

const THEME_STORAGE_KEY = 'app-theme-mode';

const getSystemTheme = (): ThemeMode =>
    matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const getInitialPreference = (): ThemePreference => {
    const themePreference = localStorage.getItem(THEME_STORAGE_KEY) as ThemePreference | null;

    switch (themePreference) {
        case 'light':
        case 'dark':
            return themePreference;
    }

    return 'system';
};

const ThemeContext = createContext<ThemeContextProps>({
    mode: 'light',
    preference: 'system',
    setPreferenceState: () => {},
    theme: lightTheme,
});

export const useThemeContext = () => useContext(ThemeContext);

export const ThemeProvider = (props: { children: ReactNode }) => {
    const [preference, setPreference] = useState<ThemePreference>(getInitialPreference);
    const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemTheme);

    // OS-Änderungen immer verfolgen
    useEffect(() => {
        const mediaQuery = matchMedia('(prefers-color-scheme: dark)');
        const mediaQueryOnChange = (event: MediaQueryListEvent) => {
            setSystemMode(event.matches ? 'dark' : 'light');
        };

        mediaQuery.addEventListener('change', mediaQueryOnChange);
        return () => mediaQuery.removeEventListener('change', mediaQueryOnChange);
    }, []);

    const setPreferenceState = useCallback((pref: ThemePreference) => {
        setPreference(pref);
        localStorage.setItem(THEME_STORAGE_KEY, pref);
    }, []);

    // effektiver Modus: entweder System oder explizite Präferenz
    const mode: ThemeMode = preference === 'system' ? systemMode : preference;

    useEffect(() => {
        document.body.classList.remove('theme-light', 'theme-dark');
        document.body.classList.add(`theme-${mode}`);
    }, [mode]);

    return (
        <ThemeContext.Provider
            value={useMemo(
                () => ({
                    mode,
                    preference,
                    setPreferenceState,
                    theme: mode === 'light' ? lightTheme : darkTheme,
                }),
                [mode, preference]
            )}
        >
            {props.children}
        </ThemeContext.Provider>
    );
};
